import { restartableTask } from 'ember-concurrency';
import { PlanningFilterDefinitions } from 'volta/components/planning/list';
import { IWidgetArgs } from 'volta/components/widgets';
import DashboardSummary from 'volta/models/dashboard-summary';
import { IDashboardWidget } from 'volta/models/types/user';
import { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { safeEqFilter } from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type RouterService from '@ember/routing/router-service';
import type { IPlanningReport } from 'volta/models/types/stats';
import type StoreService from 'volta/services/store';
import type CustomFieldDefinitionsService from 'volta/services/custom-field-definitions-service';

const PlanningReportsResourceName = {
  singular: 'planningReport',
  plural: 'planningReports'
};

interface IArgs extends IWidgetArgs {
  onWidgetUpdate?: (widget: IDashboardWidget) => void;
}

export default class PlanningReportWidget extends Component<IArgs> {
  @service router!: RouterService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;
  @service store!: StoreService;

  PlanningReportsResourceName = PlanningReportsResourceName;

  @tracked warehouse?: string;
  @tracked supplier?: string;
  @tracked deliveryWorkshop?: string;
  @tracked workshop?: string;
  @tracked acqCode?: string;
  @tracked tags?: string;
  @tracked planner?: string;
  @tracked timePeriod?: string = this.args.queryParamsValues?.['timePeriod'] ?? 'week';
  @tracked timePeriodCount?: number = this.args.queryParamsValues?.['timePeriodCount']
    ? +this.args.queryParamsValues.timePeriodCount
    : 24;
  @tracked query?: IJsonApiQuery;

  get filterDefinitions() {
    return PlanningFilterDefinitions(
      this.store,
      this.customFieldDefinitionsService,
      this.args.queryParamsValues?.['warehouse']
    );
  }

  @action
  handleTimePeriodChange({
    timePeriod,
    timePeriodCount
  }: {
    timePeriod: number;
    timePeriodCount: number;
  }) {
    this.timePeriod = `${timePeriod}`;
    this.timePeriodCount = timePeriodCount;
    this.router.transitionTo('protected.dashboard', {
      queryParams: { timePeriod, timePeriodCount }
    });
  }

  generateReport = restartableTask(async (apiQuery = {}) => {
    this.query = this.formatApiQuery(apiQuery);

    try {
      const report: IPlanningReport = await this.store.normalPost('plannings/report', this.query);

      return {
        planningsKpis: report.planningsKpis,
        dashboardSummary: new DashboardSummary(report.dashboardSummary)
      };
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  protected formatApiQuery(kpiQuery: IJsonApiQuery) {
    kpiQuery.filter = {
      ...kpiQuery.filter,
      timePeriod: safeEqFilter(this.timePeriod ?? 'week'),
      timePeriodCount: safeEqFilter(this.timePeriodCount + '' ?? '24')
    };
    kpiQuery.include = 'dashboardSummary';
    delete kpiQuery.page;
    return kpiQuery;
  }
}
